/**
 * The recipes a suggestion list may offer for one day: the library minus what
 * the cook must never be served (allergies, excluded ingredients, declared
 * diets — `useCookPreferences`) and minus any repeat of a dinner planned within
 * two weeks of that day (`useSameMealExclude`, #736).
 *
 * The « Remplacer » fans apply both through `buildFanSuggestions`' `exclude`.
 * The older desktop layout's sheets (`SwapSuggestionSheet`,
 * `RecipeSuggestionSheet`, 800–1180px) rank with `getTopSuggestions`, which
 * knows neither, so they filter through this first — the same two gates,
 * whichever screen the cook is on. For a dinner they also narrow by the day's
 * kind and time cap and the week's balance (`useSlotFit`), as the fans do.
 */
import { useMemo } from 'react';
import { useCookPreferences } from './useCookPreferences';
import { useSameMealExclude } from './useSameMealExclude';
import { useSlotFit } from './useSlotFit';
import { narrowForSlot, type RankableRecipe } from '../services/planRanking';
import type { PreferenceRecipe } from '../services/cookPreferences';
import type { IdentifiableRecipe } from '../services/mealIdentity';

/** Pure: what the two gates leave. Exported for tests. */
export function suggestionPool<T>(
  recipes: T[],
  isExcluded: ((r: T) => boolean) | null,
  repeats: ((r: T) => boolean) | null,
): T[] {
  if (!isExcluded && !repeats) return recipes;
  return recipes.filter((r) => !(isExcluded && isExcluded(r)) && !(repeats && repeats(r)));
}

/** A dinner list keeps at least this many before a day-and-week rule gives way. */
const SLOT_FIT_MIN_KEEP = 3;

/**
 * `date` is the day being filled (null while no slot is chosen); `ignoreMealId`
 * is the dish being swapped out, which is not something to keep away from;
 * `mealType` is the slot's meal — only a dinner answers to the schedule.
 */
export function useSuggestionPool<T extends PreferenceRecipe & IdentifiableRecipe & RankableRecipe>(
  recipes: T[],
  date: string | null,
  ignoreMealId?: string,
  mealType?: string,
): T[] {
  const prefs = useCookPreferences();
  const sameMealExclude = useSameMealExclude();
  const slotFitOf = useSlotFit();
  return useMemo(() => {
    const pool = suggestionPool(
      recipes,
      prefs.hasHardRules ? (r: T) => prefs.isExcluded(r) : null,
      date ? sameMealExclude(date, ignoreMealId) : null,
    );
    if (!date || mealType !== 'dinner') return pool;
    const { slot, balance } = slotFitOf(date, ignoreMealId);
    return narrowForSlot(pool, (r) => r, slot, balance, { kind: true, minKeep: SLOT_FIT_MIN_KEEP });
  }, [recipes, prefs, date, ignoreMealId, mealType, sameMealExclude, slotFitOf]);
}
